import type { TranslateFunc } from './translate-func';

const pluralForm = (locale: string, count: string | number) => {
  // Numeric check for pluralization count
  // Also, handle negative numbers, treat -1 as 1.
  const n = Math.abs(parseFloat(String(count)));
  if (isNaN(n) || !isFinite(n)) {
    return false;
  }

  switch (locale) {
    // Rule: nplurals=2; plural=(n != 1);
    // Fallthrough where the same rule applies
    case 'en':
    case 'en_GB':
    case 'en_IE':
    case 'en_NZ':
    case 'nb_NO':
    case 'sv':
    case 'fi':
    case 'es':
    case 'it':
    case 'da':
      // Rule: nplurals=2; plural=(n != 1);
      return n !== 1 ? 1 : 0;
    case 'fr':
      // Rule: nplurals=2; plural=(n > 1);
      return n > 1 ? 1 : 0;
    case 'pl':
      // Rule: nplurals=3; plural=(n==1 ? 0 : n%10>=2 && n%10<=4 && (n%100<10 || n%100>=20) ? 1 : 2);
      if (n === 1) {
        return 0;
      }
      return n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)
        ? 1
        : 2;
    default:
      return false;
  }
};

const getPluralIndex = (
  translation: string[] | string,
  locale: string,
  params: { [key: string]: any }
) => {
  const pluralIndex = pluralForm(locale, params.n);
  if (typeof pluralIndex !== 'boolean' && Array.isArray(translation)) {
    return translation[pluralIndex] !== undefined ? pluralIndex : false;
  }
  return false;
};

type Translations = {
  [domain: string]: {
    [text: string]: string;
  };
};

export const translator = (
  translations: Translations,
  locale: string
): TranslateFunc =>
  ((
    domain: string,
    text: string,
    params: any = {},
    options: { react?: boolean } = { react: true }
  ) => {
    if (!translations || !translations[domain] || !translations[domain][text]) {
      return text;
    }

    const translation = translations[domain][text];
    const pluralIndex = getPluralIndex(translation, locale, params);

    let translatedString = text;

    if (pluralIndex !== false) {
      translatedString = translation[pluralIndex];
    } else if (typeof translation === 'string') {
      translatedString = translation;
    }

    const stringWithParams = Object.keys(params).reduce(
      (string, key) =>
        string.replace(
          new RegExp(`%${!options.react ? '@?' : ''}${key}%`, 'g'),
          params[key]
        ),
      translatedString
    );

    if (!options.react) {
      return stringWithParams;
    }

    // Inject non-text components (replace placeholders that start with "@")
    const stringComponentArray = Object.keys(params).reduce(
      (stringArray, key) => {
        let newStringArray: string[] = [];
        stringArray.forEach(str => {
          if (typeof str === 'string') {
            const stringParts = str.split(`%@${key}%`);
            for (let i = stringParts.length - 1; i > 0; i -= 1) {
              stringParts.splice(i, 0, params[key]);
            }
            newStringArray = newStringArray.concat(
              stringParts.filter(item => item !== '' && item !== undefined)
            );
          } else {
            newStringArray.push(str);
          }
        });
        return newStringArray;
      },
      [stringWithParams]
    );

    // Return string if possible
    return stringComponentArray.length === 1
      ? stringComponentArray[0]
      : stringComponentArray;
  }) as TranslateFunc;
